import type { ReactElement } from 'react';
import React, { useCallback } from 'react';
import classNames from 'clsx';
import type { InternalContainerProps } from './InternalContainer.types';
import { TestIds, INTERNAL_CONTAINER_ROOT_CLASS } from './constants';
import style from './InternalContainer.module.scss';

const OverflowWrapper = React.forwardRef<
  HTMLDivElement,
  {
    className: string;
    children?: React.ReactElement;
  }
>(({ children, className }, ref) => {
  return (
    <div className={className} data-testid={TestIds.overflow} ref={ref}>
      {children}
    </div>
  );
});

const InternalContainer: React.ForwardRefRenderFunction<
  HTMLDivElement,
  InternalContainerProps
> = (
  {
    id,
    containerLayoutClassName,
    overlowWrapperClassName,
    hasOverflow,
    hasScrollOverflow,
    children,
    role,
    tabIndex: tabIndexFromProps,
    backgroundLayer = [],
    pinnedLayer = [],
    className,
    shouldOmitWrapperLayers,
    ...restOfProps
  },
  ref,
) => {
  const shouldWrapChildren = !shouldOmitWrapperLayers;
  const tabIndexWithOverflow = hasScrollOverflow ? tabIndexFromProps || 0 : -1;

  const tabIndex = hasOverflow ? tabIndexWithOverflow : undefined;
  const hasPinnedLayer = pinnedLayer && pinnedLayer.length > 0;

  const wrapWithOverflowWrapperIfNeeded = useCallback(
    (reactChildren: ReactElement) =>
      hasOverflow ? (
        <OverflowWrapper
          className={classNames(overlowWrapperClassName, style.overflowWrapper)}
        >
          {reactChildren}
        </OverflowWrapper>
      ) : (
        reactChildren
      ),
    [hasOverflow, overlowWrapperClassName],
  );

  const contentProps: Record<string, any> = {
    ref,
    'data-testid': TestIds.content,
    tabIndex,
    ...(role ? { role } : {}),
    className: containerLayoutClassName,
    ...(restOfProps['data-scope'] && {
      'data-scope': restOfProps['data-scope'],
    }),
    ...(restOfProps['data-repeater-items-indexes'] && {
      'data-repeater-items-indexes': restOfProps['data-repeater-items-indexes'],
    }),
  };

  const renderedChildren =
    typeof children === 'function' ? children() : children;

  return (
    <div
      id={id}
      className={classNames(
        INTERNAL_CONTAINER_ROOT_CLASS,
        style.internalContainerRoot,
        className,
        !shouldWrapChildren && containerLayoutClassName,
      )}
      data-testid={TestIds.root}
    >
      <div
        className={style.backgroundLayer}
        data-testid={TestIds.backgroundLayer}
      >
        {backgroundLayer}
      </div>
      {hasPinnedLayer && (
        <div className={style.pinnedLayer} data-testid={TestIds.pinnedLayer}>
          {pinnedLayer}
        </div>
      )}
      {wrapWithOverflowWrapperIfNeeded(
        shouldWrapChildren ? (
          <div {...contentProps}>{renderedChildren}</div>
        ) : (
          <React.Fragment>{renderedChildren}</React.Fragment>
        ),
      )}
    </div>
  );
};

export default React.forwardRef(InternalContainer);
